import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];
export function SHELL({ children, className = "" }) {
    return <div className={`max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 ${className}`}>{children}</div>;
}

export function Reveal({ children, delay = 0, y = 28, className }) {
    const reduced = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduced ? false : { opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

export function Mono({ children, className = "" }) {
    return <span className={`font-mono text-[10px] tracking-[0.3em] uppercase ${className}`}>{children}</span>;
}

export function SectionHead({ index, label, title, sub, center }) {
    return (
        <div className={`mb-14 md:mb-20 ${center ? "text-center" : ""}`}>
            <Reveal>
                <div className={`flex items-center gap-4 mb-6 ${center ? "justify-center" : ""}`}>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-vera-gold">{index}</span>
                    <span className="hairline w-14" aria-hidden="true" />
                    <span className="font-mono text-[10px] tracking-[0.3em] text-vera-mut uppercase">{label}</span>
                </div>
            </Reveal>
            <Reveal delay={0.06}>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] whitespace-pre-line">{title}</h2>
            </Reveal>
            {sub && (
                <Reveal delay={0.12}>
                    <p className={`mt-5 max-w-2xl text-vera-mut text-sm sm:text-base leading-relaxed ${center ? "mx-auto" : ""}`}>{sub}</p>
                </Reveal>
            )}
        </div>
    );
}

export function PageHero({ label, title, sub, testid }) {
    return (
        <section className="relative pt-36 pb-14 md:pt-44 md:pb-20 border-b border-white/10 overflow-hidden" data-testid={testid}>
            <div className="absolute inset-0 vera-grid-bg" aria-hidden="true" />
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[900px] h-[440px] rounded-full bg-vera-blue/10 blur-[140px]" aria-hidden="true" />
            <SHELL className="relative">
                <div className="relative">
                    <Reveal y={16}>
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-vera-gold" aria-hidden="true" />
                            <Mono className="text-vera-mut">{label}</Mono>
                        </div>
                    </Reveal>
                    <Reveal delay={0.06}>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] whitespace-pre-line">{title}</h1>
                    </Reveal>
                    {sub && (
                        <Reveal delay={0.14}>
                            <p className="mt-6 max-w-2xl text-vera-mut text-sm sm:text-base leading-relaxed">{sub}</p>
                        </Reveal>
                    )}
                </div>
            </SHELL>
        </section>
    );
}

const BTN = "inline-flex items-center justify-center gap-2.5 font-mono text-[11px] tracking-[0.22em] px-7 py-3.5 transition-all duration-300 select-none";
const BTN_BASE = { gold: "border border-vera-gold/60 text-vera-gold2 bg-vera-gold/5 hover:bg-vera-gold/15 hover:border-vera-gold", blue: "border border-vera-blue/60 text-[#A8D2FF] bg-vera-blue/10 hover:bg-vera-blue/25 hover:border-vera-blue", ghost: "border border-white/15 text-vera-white hover:border-vera-soft/70 hover:text-white" };

export function Btn({ variant = "ghost", to, href, onClick, children, testid, className = "", external, disabled }) {
    const cls = `${BTN} ${BTN_BASE[variant]} ${disabled ? "opacity-40 pointer-events-none" : ""} ${className}`;
    if (href) {
        return (
            <a href={href} data-testid={testid} className={cls} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                {children}
            </a>
        );
    }
    if (to) {
        return (
            <Link to={to} data-testid={testid} className={cls} onClick={onClick}>
                {children}
            </Link>
        );
    }
    return (
        <button type="button" data-testid={testid} className={cls} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );
}

const TONES = {
    LIVE: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10",
    BUILDING: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10",
    PLANNED: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10",
    "COMING SOON": "text-[#55B7FF] border-[#55B7FF]/40 bg-[#55B7FF]/10",
    DEMO: "text-vera-gold2 border-vera-gold/40 bg-vera-gold/10",
    EXAMPLE: "text-vera-gold2 border-vera-gold/40 bg-vera-gold/10",
};

export function StatusBadge({ status, className = "" }) {
    const tone = TONES[status] || TONES["LIVE"];
    return (
        <span className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] px-3 py-1 rounded-full border ${tone} ${className}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
            {status}
        </span>
    );
}

export function TiltCard({ children, className = "", testid }) {
    const ref = useRef(null);
    const onMove = (e) => {
        if (window.matchMedia("(pointer: coarse)").matches || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ref.current.style.transform = `perspective(900px) rotateX(${(-py * 5).toFixed(2)}deg) rotateY(${(px * 6).toFixed(2)}deg) translateY(-3px)`;
    };
    const onLeave = () => {
        if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
    };
    return (
        <div ref={ref} data-testid={testid} onMouseMove={onMove} onMouseLeave={onLeave} className={`transition-transform duration-200 ease-out will-change-transform ${className}`}>
            {children}
        </div>
    );
}

export function XLogo({ className = "w-4 h-4" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

export function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
}
